import React from 'react';
import {useSalesManagement} from '../hooks/useSalesManagement';
import SalesTable from '../components/SalesTable';
import SalesStatistics from '../components/SalesStatistics';
import {AppText, AppTitle, AppBodyText} from '../../../Presentation/components/ui/Typography';
import Button from '../../../Presentation/components/ui/Button';
import {AlertCircle, Receipt, TrendingUp} from 'lucide-react';
import SidebarLayout from '../../dashboard/components/SidebarLayout';
import {PermissionDeniedCard} from '../../../Presentation/components/common';
import Card from '../../../Presentation/components/ui/Card';
import Tabs from '../../../Presentation/components/ui/Tabs';
import {useAuthorization} from '../../auth/hooks/useAuthorization';
import {PERMISSIONS} from '../../../constants/permissions';

type ActiveTab = 'sales' | 'statistics';

const SalesManagementPage: React.FC = () => {
  const {hasPermission} = useAuthorization();

  const {
    currentPage,
    pageSize,
    searchText,
    sales,
    statistics,
    pagination,
    loading,
    error,
    handleTabChange,
    handlePageChange,
    handlePageSizeChange,
    handleSearchChange,
    handleViewDetails,
    handleRefresh,
    clearError
  } = useSalesManagement();

  const tabConfig = [
    {
      key: 'sales' as const,
      label: 'المبيعات',
      description: 'عرض وإدارة جميع فواتير المبيعات',
      icon: <Receipt className="w-5 h-5" />,
      badge: sales?.length || 0,
      permission: PERMISSIONS.MANAGE_SALES
    },
    {
      key: 'statistics' as const,
      label: 'إحصائيات المبيعات',
      description: 'إحصائيات شاملة للمبيعات والتحليلات',
      icon: <TrendingUp className="w-5 h-5" />,
      permission: PERMISSIONS.VIEW_FINANCIAL_REPORTS
    }
  ];
  const visibleTabs = tabConfig.filter(tab => !tab.permission || hasPermission(tab.permission));

  const paginatedData = {
    pagination: {
      page: currentPage,
      limit: pageSize,
      total: pagination.total
    }
  };

  const salesContent = (
    <div className="space-y-6 p-6">
      <SalesTable
        sales={sales}
        loading={loading}
        paginatedData={paginatedData}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onViewDetails={handleViewDetails}
        searchValue={searchText}
        onSearchChange={handleSearchChange}
        onRefresh={handleRefresh}
        refreshing={loading}
      />
    </div>
  );

  const statisticsContent = (
    <div className="space-y-6 p-6">
      <SalesStatistics statistics={statistics} loading={loading} />
    </div>
  );

  const tabItems = visibleTabs.map(tab => ({
    key: tab.key,
    label: tab.label,
    description: tab.description,
    icon: tab.icon,
    badge: tab.badge,
    content: tab.key === 'sales' ? salesContent : statisticsContent
  }));

  const defaultKey = visibleTabs[0]?.key ?? 'sales';

  return (
    <SidebarLayout>
      <div className="min-h-full flex flex-col gap-6">
        <div className="mb-6">
          <div className="flex justify-between items-center">
            <div>
              <AppTitle className="font-bold text-gray-900 mb-2">إدارة الفواتير</AppTitle>
              <AppText className="text-gray-600">
                عرض وإدارة جميع فواتير المبيعات في النظام
              </AppText>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <AppText className="font-medium text-red-800">حدث خطأ في تحميل البيانات</AppText>
                  <AppBodyText className="text-red-600">{error}</AppBodyText>
                </div>
              </div>
              <Button variant="outline" size="small" onClick={clearError}>
                إغلاق
              </Button>
            </div>
          </div>
        )}

        {!hasPermission(PERMISSIONS.READ_SALES) &&
        !hasPermission(PERMISSIONS.MANAGE_SALES) &&
        !hasPermission(PERMISSIONS.VIEW_FINANCIAL_REPORTS) ? (
          <PermissionDeniedCard message="ليس لديك صلاحيات للوصول إلى إدارة المبيعات. يرجى التواصل مع المدير للحصول على الصلاحيات المناسبة." />
        ) : (
          <Card className="p-0 overflow-hidden shadow-sm">
            <Tabs
              items={tabItems}
              defaultActiveKey={defaultKey}
              onChange={(key) => handleTabChange(key as ActiveTab)}
              variant="modern"
              className="flex-1"
            />
          </Card>
        )}
      </div>
    </SidebarLayout>
  );
};

export default SalesManagementPage;
